import React from 'react';
import clsx from 'clsx';
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import { Link } from '@tanstack/react-router';
import '@/types/table.types';
import { TradeItem, getRateLabel, TRADE_STATUS_COLORS, TRADE_STATUS_LABELS, COLLATERAL_TYPE_LABELS, CollateralType } from '@/types';
import { BO_MAX_RETRIES, STATUS_TOOLTIPS, TERMINAL_TRADE_STATUSES } from '@/types/trades.types';
import { getEffectiveTradeStatus } from '@/utils/trades/effective-status';
import {
  boPostingHeadline as stuckBoPostingTitle,
  resolveBoReason,
} from '@/utils/trades/bo-posting-copy';
import { TruncatedCell } from '@/components/ui/TruncatedCell';
import { isMtmRequired } from '@/utils/trades/trade-math';
import { showsClosingCash } from '@/utils/trades/closing-cash';
import { RATE_LEG_CELLS } from '@/utils/trades/rate-legs';
import {
  getBookedFxRate,
  getDisplayedBookedFxRate,
  getDisplayedCobFxRate,
  getEffectiveSettlementCurrency,
  getNativeAmount,
  getSettlementDisplay,
  type SettlementMoneyField,
} from '@/utils/trades/settlement-ccy';
import { formatDecimalString } from '@/utils/decimal-display';
import { formatDate, formatDateTimeUtc, formatDateTimeLocal } from '@/utils/format';

const columnHelper = createColumnHelper<TradeItem>();

const formatCurrencyAmount = (value: number | null | undefined): string => {
  // Guard non-finite (NaN/Infinity) as well as null/undefined: a coerced backend
  // value can arrive as NaN, which would otherwise render as the literal "NaN" (QA finding D).
  if (value === null || value === undefined || !Number.isFinite(value)) return '-';
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

// Fee Income / Cash Pool Interest Accrual / Net Amount: daily figures in the security ccy.
const rateLegCell = (value: number | null | undefined, currency: string | undefined) => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return <span className="text-gray-400">-</span>;
  }
  return (
    <div className="text-right">
      <span className="font-medium text-gray-900">{formatCurrencyAmount(value)}</span>
      <span className="text-xs text-gray-500 ml-1">{currency}</span>
    </div>
  );
};

// LR-474: settlement-ccy is the primary line of the money cells; the original
// security-ccy figure lives in a hover tooltip
// ("<native> <SEC_CCY> @ FX <fx_rate> (SEC→SETTLE)") shown only when the
// displayed currency actually differs from the security's (no tooltip on
// same-ccy rows or on the honest native fallback). The booked fx_rate is the
// security→settlement multiplier; the arrow disambiguates it from the inverted
// market-pair quote the detail tab shows (LR-494 / kvi review #1).
const nativeAmountTitle = (
  t: TradeItem,
  field: SettlementMoneyField,
  displayCcy: string,
  fmt: (n: number) => string,
): string | undefined => {
  const native = getNativeAmount(t, field);
  if (native == null || displayCcy === (t.currency || '').toUpperCase()) return undefined;
  const fx = getBookedFxRate(t);
  // 'FX' labels the bare number — "@ 1.085" alone reads as "at what?" (r1 #12).
  // The SEC→SETTLE arrow flags the direction so this booked multiplier (e.g.
  // 0.92, native×fx → settlement) isn't misread as the inverted EUR/USD market
  // quote the detail tab shows for the same trade (LR-494 / kvi review #1).
  const dir = `${(t.currency || '').toUpperCase()}→${displayCcy}`;
  return `${fmt(native)} ${t.currency}${fx != null ? ` @ FX ${fx} (${dir})` : ''}`;
};

// LR-521: tooltip for the COB value cells. Unlike the booking columns above,
// their settlement figure is the server twin converted at the COB FX rate
// (`cob_fx_rate`, falling back to the booked `fx_rate` — the same rule the
// backend `_cob_to_settlement` and `getSettlementDisplay` use), so this must
// cite THAT rate, not the booked one — else `native × cited-fx` disagrees with
// the amount shown on any row where cob_fx_rate ≠ fx_rate. Like the booked
// multiplier, cob_fx_rate is security→settlement (the arrow disambiguates it
// from the inverted market-pair quote in the COB FX Rate column); when it fell
// back to the booked rate the tooltip flags it so the cited number is honest.
const cobNativeAmountTitle = (
  t: TradeItem,
  field: SettlementMoneyField,
  displayCcy: string,
  fmt: (n: number) => string,
): string | undefined => {
  const native = getNativeAmount(t, field);
  if (native == null || displayCcy === (t.currency || '').toUpperCase()) return undefined;
  const cob = t.cob_fx_rate;
  const usingCob = cob != null && Number.isFinite(cob) && cob > 0;
  const fx = usingCob ? cob! : getBookedFxRate(t);
  const dir = `${(t.currency || '').toUpperCase()}→${displayCcy}`;
  if (fx == null) return `${fmt(native)} ${t.currency}`;
  const src = usingCob ? 'COB' : 'booked, COB FX unavailable';
  return `${fmt(native)} ${t.currency} @ FX ${fx} (${dir}, ${src})`;
};

// Tooltip label for a leg's settled-at stamp: canonical UTC, em dash while the leg
// is unsettled (the raw ISO string used to leak into the Partially Settled tooltip).
const legSettledAtLabel = (settledAt: string | null | undefined): string =>
  (settledAt && formatDateTimeUtc(settledAt)) || '—';

// LR-404: per-leg settlement marker. For FOP trades cash and securities can
// settle in different places at different times; renders status pill plus
// timestamp and settlement-place name when available.
const renderSettlementLeg = (
  isSettled: boolean | undefined,
  settledAt: string | null | undefined,
  placeName: string | null | undefined,
): React.ReactNode => {
  const badge = isSettled ? (
    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-green-100 text-green-800">
      ✓ Settled
    </span>
  ) : (
    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800">
      ⌛ Pending
    </span>
  );
  // The leg time is the operator's Settle click — an operational timestamp, not the
  // contract value-date the trade card leads with. Canonical UTC like Created At /
  // Updated At, viewer-local clock on hover; the old local 12-hour string carried no
  // zone marker and could land on the neighbouring calendar day for a non-UTC desk.
  const timeStr = isSettled && settledAt ? formatDateTimeUtc(settledAt) : '';
  const localTitle = timeStr ? `Local time: ${formatDateTimeLocal(settledAt as string)}` : undefined;
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5">
        {badge}
        {timeStr && <span className="text-[10px] text-gray-500" title={localTitle}>{timeStr}</span>}
      </div>
      {placeName && (
        <span className="text-[10px] text-gray-500 uppercase">{placeName}</span>
      )}
    </div>
  );
};

export const createTradesColumns = (options?: {
  onSearchClick?: (value: string) => void;
  onShowBoError?: (trade: TradeItem) => void;
  /** LR-478: origin signal written onto the trade-ID Link's search, so the
   *  single-trade breadcrumb knows where the row was opened from (and where to
   *  return). Optional — bare createTradesColumns() callers keep compiling and
   *  just produce an originless Link (breadcrumb then defaults to "Trades"). */
  origin?: { from?: 'trades' | 'mtm' | 'history'; cp?: number };
  // TanStack ColumnDef's TValue is invariant; using `unknown` rejects heterogeneous accessor columns. `any` is the documented escape hatch.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): ColumnDef<TradeItem, any>[] => {
  const { onShowBoError, origin } = options ?? {};
  return [
  columnHelper.accessor('id', {
    header: 'ID',
    cell: (info) => {
      const value = info.getValue();
      // SPA transition to the single-trade page — no full reload, so AuthGuard stays
      // mounted and there is no "Checking authentication…" flash. This previously
      // opened then instantly reverted to /trades: the Trades view→URL sync
      // (use-url-view-sync) guarded on `pathname.startsWith('/trades')`, which also
      // matched `/trades/$tradeId` and navigated back. Fixed in that hook.
      return (
        <Link
          to="/trades/$tradeId"
          params={{ tradeId: String(value) }}
          // LR-478: carry where this row was opened from into the detail page's
          // breadcrumb. `from` undefined (originless caller) → breadcrumb defaults
          // to "Trades"; `cp` only meaningful for the MTM origin.
          search={{ from: origin?.from, cp: origin?.cp }}
          className="text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        >
          {value}
        </Link>
      );
    },
    size: 80,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('status_display', {
    header: 'Status',
    cell: (info) => {
      const row = info.row.original;
      const statusLower = row.status;
      // LR-497: label/colour/tooltip all key off the DERIVED effective status so
      // the badge matches the sidebar filter and detail-header pill. The two
      // computed buckets (partially_settled / pending_partial_settlement) render
      // as proper pills here — superseding the former inline ½ glyph (LR-430).
      const effective = getEffectiveTradeStatus(row);
      const label = TRADE_STATUS_LABELS[effective];
      // Single source of the status→colour map (LR-208 r6 LOW): build from the
      // shared TRADE_STATUS_COLORS instead of a byte-for-byte inline copy that
      // could drift from StatusPill / the detail header.
      const c = TRADE_STATUS_COLORS[effective];
      const badgeClass = c ? `${c.bg} ${c.text}` : 'bg-gray-100 text-gray-800';
      // LR-408: BO failure tracking. The tooltip surfaces the reason so the
      // operator sees it without diving into Sentry / logs. Retry counter chip
      // warns about pending-with-prior-failures (not yet rejected). Lifecycle
      // actions live in the FloatingActionBar.
      // These key off the RAW status, unaffected by the derived label.
      //
      // LR-712: the reason comes from the posting's own state, not from
      // `last_bo_error`. The column is one overwritable string per trade, so on a
      // trade with more than one failed posting it named whichever failed LAST —
      // which is how TRD-111 showed "account not found" while it was actually
      // stuck on a timed-out transfer. `bo_posting` says WHICH posting, and the
      // legacy string is read only when there is no posting record to contradict
      // (a trade rejected before LR-709 shipped the attempt log).
      const retryCount = row.bo_retry_count ?? 0;
      const { posting, legacy: legacyError } = resolveBoReason(row);
      const isRejected = statusLower === 'rejected';
      const showRetryCounter = statusLower === 'pending' && retryCount > 0;
      const partiallySettled = effective === 'partially_settled';
      const canOpenBoError = (!!posting || !!legacyError) && !!onShowBoError;
      const boReason = posting
        ? stuckBoPostingTitle(posting)
        : legacyError && `BO error: ${legacyError}`;
      const errorClickHandler = canOpenBoError
        ? (e: React.MouseEvent) => { e.stopPropagation(); onShowBoError(row); }
        : undefined;
      // LR-497: whitespace-nowrap keeps each pill on a single line. The grid rows
      // are fixed-height (VirtualizedGrid), so a wrapped label/chip would overflow
      // into the row below — the longer trader-facing labels ("Pending Partial
      // Settlement") + the retry chip made the old 100px column wrap and overlap.
      const badgeBaseClass = `inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass}`;
      const counterChipClass = 'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium bg-orange-100 text-orange-800';
      return (
        <div className="flex items-center gap-2 flex-wrap">
          {isRejected && canOpenBoError ? (
            <button
              type="button"
              onClick={errorClickHandler}
              className={`${badgeBaseClass} cursor-pointer hover:opacity-80 transition-opacity`}
              title="Click to view full BO error."
            >
              {label}
            </button>
          ) : (
            <span
              className={badgeBaseClass}
              title={
                isRejected && boReason
                  ? boReason
                  : partiallySettled
                    ? `Partially Settled · Cash: ${legSettledAtLabel(row.cash_settled_at)} · Securities: ${legSettledAtLabel(row.securities_settled_at)}`
                    : STATUS_TOOLTIPS[effective]
              }
            >
              {label}
            </span>
          )}
          {showRetryCounter && (
            canOpenBoError ? (
              <button
                type="button"
                onClick={errorClickHandler}
                className={`${counterChipClass} cursor-pointer hover:opacity-80 transition-opacity`}
                title="Click to view full BO error."
              >
                retry {retryCount}/{BO_MAX_RETRIES}
              </button>
            ) : (
              <span
                className={counterChipClass}
                title={boReason || 'Previous BO call failed; will retry hourly.'}
              >
                retry {retryCount}/{BO_MAX_RETRIES}
              </span>
            )
          )}
          {/* LR-497: Partially Settled / Pending Partial Settlement now render as
              their own pill label (above) via the derived effective status. */}
        </div>
      );
    },
    // LR-497: widened from 100 → 220 to fit the longest trader-facing label
    // ("Pending Partial Settlement") and the "Pending Trade Date" + retry-chip
    // combination on a single line (rows are fixed-height — see badgeBaseClass).
    size: 220,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('symbol_id', {
    header: 'Symbol',
    cell: (info) => <span className="font-medium text-gray-900">{info.getValue()}</span>,
    size: 120,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('description', {
    header: 'Description',
    cell: (info) => {
      const value = info.getValue();
      return (
        <TruncatedCell
          content={<span className="text-gray-700">{value}</span>}
          plainText={value}
        />
      );
    },
    size: 200,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('isin', {
    header: 'ISIN',
    cell: (info) => {
      const value = info.getValue();
      return (
        <TruncatedCell
          content={<span className=" text-xs text-gray-500">{value}</span>}
          plainText={value}
        />
      );
    },
    size: 130,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('counterparty', {
    header: 'Counterparty',
    cell: (info) => {
      const value = info.getValue();
      return (
        <TruncatedCell
          content={<span className="font-medium text-blue-600">{value}</span>}
          plainText={value}
        />
      );
    },
    size: 150,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('agreement_display', {
    header: 'Agreement',
    cell: (info) => {
      const value = info.getValue();
      return (
        <TruncatedCell
          content={<span className="text-gray-700">{value}</span>}
          plainText={value}
        />
      );
    },
    size: 90,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('trade_type_display', {
    header: 'Trade Type',
    cell: (info) => {
      const value = info.getValue();
      // LR-428: colour each trade type distinctly (mirrors Django admin):
      // lend → green, borrow → red, repo → blue, reverse_repo → slate.
      // Agreement column stays gray to avoid duplicating the colour.
      const tradeType = info.row.original.trade_type;
      const tradeTypeBadgeClass: Record<string, string> = {
        lend: 'bg-green-100 text-green-800',
        borrow: 'bg-red-100 text-red-800',
        repo: 'bg-blue-100 text-blue-800',
        reverse_repo: 'bg-slate-200 text-slate-700',
      };
      const badgeClass = tradeTypeBadgeClass[tradeType ?? ''] || 'bg-gray-100 text-gray-700';
      return (
        <span className={`inline-flex items-center rounded-sm px-1.5 py-0 text-[10px] font-medium whitespace-nowrap ${badgeClass}`}>
          {value}
        </span>
      );
    },
    size: 130,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('term_type_display', {
    header: 'Trade Term',
    cell: (info) => {
      const termType = info.row.original.term_type;
      const display = info.getValue() || (termType === 'evergreen' ? 'Evergreen' : termType === 'open' ? 'Open' : 'Term');
      const noticeServed = info.row.original.notice_served;
      const maturityDate = info.row.original.maturity_date;

      // LR-428: pill-badge with non-Status palette (Status owns
      // cyan/teal/blue/green/red). Different term types have distinct
      // lifecycle behaviours — colour aids quick scanning.
      const termBadgeClass: Record<string, string> = {
        evergreen: 'bg-emerald-100 text-emerald-800',
        open: 'bg-violet-100 text-violet-800',
        term: 'bg-slate-200 text-slate-700',
      };
      const badgeClass = termBadgeClass[termType ?? ''] || 'bg-gray-100 text-gray-700';

      // LR-425: notice-served indicator collapsed from a full text badge into a
      // small amber dot + tooltip. The previous badge ("Terminating: 28 days left")
      // overflowed the Trade Term column whenever Trade Term was narrowed.
      // LR-428: hide the marker on terminal statuses (closed / cancelled) — the
      // position is already finished, notice-served info is no longer actionable.
      const status = info.row.original.status;
      const isTerminal = (TERMINAL_TRADE_STATUSES as readonly string[]).includes(status);
      let terminatingTooltip: string | null = null;
      if (noticeServed && maturityDate && !isTerminal) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const maturity = new Date(maturityDate);
        maturity.setHours(0, 0, 0, 0);
        const diffTime = maturity.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 0) {
          terminatingTooltip = `Notice served · ${diffDays} day${diffDays > 1 ? 's' : ''} to maturity`;
        } else if (diffDays === 0) {
          terminatingTooltip = 'Notice served · matures today';
        } else {
          terminatingTooltip = 'Terminated';
        }
      }

      return (
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center rounded-sm px-1.5 py-0 text-[10px] font-medium whitespace-nowrap ${badgeClass}`}>
            {display}
          </span>
          {terminatingTooltip && (
            <span
              className="inline-block h-3 w-3 rounded-full bg-amber-500 ring-2 ring-amber-200 flex-shrink-0"
              role="img"
              title={terminatingTooltip}
              aria-label={terminatingTooltip}
            />
          )}
        </div>
      );
    },
    size: 110,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('quantity', {
    header: 'Quantity',
    cell: (info) => {
      const value = info.getValue();
      if (value === null || value === undefined) return <span className="text-gray-400">-</span>;
      return (
        <span className="font-medium text-gray-900 text-right block">
          {value.toLocaleString('en-US')}
        </span>
      );
    },
    size: 90,
    meta: { align: 'right' },
  }),
  // LR-457/522: Trade Gross Price = booking gross_price (security ccy). Shown on
  // both the Trades blotter and the MtM grid — LR-522 un-gated it (was MtM-only
  // "Contract Price") as the Trade-stage twin of COB / Last Marked Gross Price.
  columnHelper.accessor('gross_price', {
    header: 'Trade Gross Price',
    cell: (info) => {
      const price = info.getValue();
      if (price === null || price === undefined)
        return <span className="text-gray-400">-</span>;
      const currency = info.row.original.currency;
      const formatted = price.toFixed(2);
      return (
        <div className="text-right">
          <span className="font-medium text-gray-900">{formatted}</span>
          <span className=" text-xs text-gray-500 ml-1">{currency}</span>
        </div>
      );
    },
    size: 150,
    meta: { align: 'right' },
  }),
  // LR-522: Trade FX Rate = the booked fx_rate, displayed as the market pair
  // settlement/security (1/rate) like the COB FX Rate column. Cross-ccy rows only;
  // dash otherwise. Single source of truth `getDisplayedBookedFxRate` so cell,
  // copy and CSV agree.
  columnHelper.accessor('fx_rate', {
    header: 'Trade FX Rate',
    cell: (info) => {
      const t = info.row.original;
      const value = getDisplayedBookedFxRate(t);
      if (value == null) return <span className="text-gray-400">-</span>;
      const pair = `${getEffectiveSettlementCurrency(t)}/${(t.currency || '').toUpperCase()}`;
      return (
        <div className="text-right" title={`Booked FX rate ${pair}`}>
          <span className="font-medium text-gray-900">{formatDecimalString(value, { dp: 6 })}</span>
          <span className="text-xs text-gray-500 ml-1">{pair}</span>
        </div>
      );
    },
    size: 150,
    meta: { align: 'right' }, // not searchable — converted-display (see COB FX Rate)
  }),
  columnHelper.accessor('cob_price', {
    // LR-393: rolling per-symbol T-1 close from TickDB daily candles
    // (refreshed daily), not the frozen booking price.
    // LR-521: renamed "COB Price (T-1)" → "COB Gross Price" — the COB twin of
    // Trade Gross Price. Stays in the security ccy (gross prices are quoted in
    // the instrument currency); its margin price + values convert to settlement.
    header: 'COB Gross Price',
    cell: (info) => {
      const price = info.getValue();
      if (price === null || price === undefined) return <span className="text-gray-400">-</span>;
      const currency = info.row.original.currency;
      const formatted = price.toFixed(2);
      return (
        <div className="text-right">
          <span className="font-medium text-gray-900">{formatted}</span>
          <span className=" text-xs text-gray-500 ml-1">{currency}</span>
        </div>
      );
    },
    size: 140,
    meta: { align: 'right' },
  }),
  // LR-494: COB (T-1 close) FX rate from TickDB quote_candles, security →
  // settlement (the COB twin of the booked fx_rate). Cross-ccy rows only;
  // falls back to the booked fx_rate (amber) when the COB rate is unavailable
  // (TickDB miss / unsupported pair). COB price stays in the security ccy and
  // the money columns stay settlement-ccy — this rate relates the two.
  columnHelper.accessor('cob_fx_rate', {
    header: 'COB FX Rate',
    cell: (info) => {
      const t = info.row.original;
      // Display value (1/rate, COB→booked fallback) resolved by the shared
      // helper so the cell, copy and CSV export agree.
      const display = getDisplayedCobFxRate(t);
      if (display == null) return <span className="text-gray-400">-</span>;
      // LR-494: display the market pair settlement/security (e.g. "EUR/USD").
      const pair = `${getEffectiveSettlementCurrency(t)}/${(t.currency || '').toUpperCase()}`;
      const title = display.usingCob
        ? `COB (T-1 close) FX rate ${pair}`
        : `Booked FX rate ${pair} — COB FX rate unavailable`;
      return (
        <div className={clsx('text-right', !display.usingCob && 'cursor-help')} title={title}>
          <span className={clsx('font-medium', display.usingCob ? 'text-gray-900' : 'text-amber-700')}>
            {formatDecimalString(display.value, { dp: 6 })}
          </span>
          <span className="text-xs text-gray-500 ml-1">{pair}</span>
        </div>
      );
    },
    size: 150,
    meta: { align: 'right' },
  }),
  // LR-521: COB (T-1) margin price + gross/margin VALUES derived from cob_price.
  // Settlement-ccy primary (converted twin computed server-side at the COB FX
  // rate, fallback booked fx_rate), native security-ccy figure in the tooltip.
  // Same money-cell doctrine as Margin Price / Contract Value, but the tooltip
  // uses `cobNativeAmountTitle` (cites the COB FX rate, not the booked one) so
  // it reconciles with the COB-rate-converted amount shown. COB *Gross Price*
  // itself is the `cob_price` column above (kept in the security ccy).
  columnHelper.accessor('cob_margin_price', {
    header: 'COB Margin Price',
    cell: (info) => {
      const t = info.row.original;
      const d = getSettlementDisplay(t, 'cob_margin_price');
      if (d === null) return <span className="text-gray-400">-</span>;
      const title = cobNativeAmountTitle(t, 'cob_margin_price', d.ccy, (n) => n.toFixed(2));
      return (
        <div className={clsx('text-right', title && 'cursor-help')} title={title}>
          <span className="font-medium text-gray-900">{d.value.toFixed(2)}</span>
          <span className=" text-xs text-gray-500 ml-1">{d.ccy}</span>
        </div>
      );
    },
    size: 150,
    // Converted-display figure (settlement ccy) — the search surface is server-side
    // (BlotterSearchFilter over id + identity text + status/type labels), so no column
    // here carries `meta.searchable`; the flag has no runtime consumer on the blotter.
    meta: { align: 'right' },
  }),
  columnHelper.accessor('cob_gross_value', {
    header: 'COB Gross Value',
    cell: (info) => {
      const t = info.row.original;
      const d = getSettlementDisplay(t, 'cob_gross_value');
      if (d === null) return <span className="text-gray-400">-</span>;
      const fmt = (n: number): string =>
        n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const title = cobNativeAmountTitle(t, 'cob_gross_value', d.ccy, fmt);
      return (
        <div className={clsx('text-right', title && 'cursor-help')} title={title}>
          <span className="font-medium text-gray-900">{fmt(d.value)}</span>
          <span className=" text-xs text-gray-500 ml-1">{d.ccy}</span>
        </div>
      );
    },
    size: 160,
    meta: { align: 'right' }, // not searchable — converted-display (see COB Margin Price)
  }),
  columnHelper.accessor('cob_margin_value', {
    header: 'COB Margin Value',
    cell: (info) => {
      const t = info.row.original;
      const d = getSettlementDisplay(t, 'cob_margin_value');
      if (d === null) return <span className="text-gray-400">-</span>;
      const fmt = (n: number): string =>
        n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const title = cobNativeAmountTitle(t, 'cob_margin_value', d.ccy, fmt);
      return (
        <div className={clsx('text-right', title && 'cursor-help')} title={title}>
          <span className="font-medium text-gray-900">{fmt(d.value)}</span>
          <span className=" text-xs text-gray-500 ml-1">{d.ccy}</span>
        </div>
      );
    },
    size: 165,
    meta: { align: 'right' }, // not searchable — converted-display (see COB Margin Price)
  }),
  columnHelper.accessor('current_market_price', {
    header: 'Current Market Price',
    cell: (info) => {
      const price = info.getValue();
      if (price === null || price === undefined) return <span className="text-gray-400">-</span>;
      const t = info.row.original;
      const currency = t.currency;
      const formatted = price.toFixed(2);
      // LR-307 (relocated from the retired Total Value column, LR-522): amber
      // "MTM" badge when a repo's live price has drifted >5% from the booked
      // gross — a hint to consider booking an MTM call. isMtmRequired only fires
      // when current_market_price is present, so the badge always has a value beside it.
      const mtmRequired = isMtmRequired(t);
      // LR-532: "as of" tooltip — when the live price was last pulled/refreshed.
      // Stays on the last successful pull, so a symbol with no recent quote
      // honestly reads as staler than the rest. Null timestamp -> no tooltip.
      // Canonical UTC (desk standard, LR-485) + a viewer-local companion line,
      // mirroring the DateTimePicker "Local time:" convention.
      const updatedAt = t.current_market_price_updated_at;
      const asOfTitle = updatedAt
        ? `Price as of ${formatDateTimeUtc(updatedAt)}\nLocal time: ${formatDateTimeLocal(updatedAt)}`
        : undefined;
      return (
        <div className="text-right" title={asOfTitle}>
          {mtmRequired && (
            <span
              data-testid="mtm-required-badge"
              title="Current market price has drifted more than 5% from the booked gross price — consider booking an MTM call."
              className="mr-1 inline-flex items-center rounded bg-amber-100 px-1 py-[1px] text-[10px] font-semibold uppercase tracking-wide text-amber-800"
            >
              MTM
            </span>
          )}
          <span className="font-medium text-gray-900">{formatted}</span>
          <span className=" text-xs text-gray-500 ml-1">{currency}</span>
        </div>
      );
    },
    size: 170,
    meta: { align: 'right' },
  }),
  // LR-306/522: "Last Marked Gross Price" sits right after Current Market Price.
  // Reads `latest_mark_price` (raw, no current_market_price fallback) — honest
  // "—" (security ccy) when never marked. Trade-stage twin is Trade Gross Price;
  // the Last Marked margin price + values follow.
  columnHelper.accessor('latest_mark_price', {
    header: 'Last Marked Gross Price',
    cell: (info) => {
      const price = info.getValue();
      if (price === null || price === undefined) return <span className="text-gray-400">-</span>;
      const currency = info.row.original.currency;
      const formatted = price.toFixed(2);
      return (
        <div className="text-right">
          <span className="font-medium text-gray-900">{formatted}</span>
          <span className=" text-xs text-gray-500 ml-1">{currency}</span>
        </div>
      );
    },
    size: 170,
    meta: { align: 'right' },
  }),
  // LR-522: Last Marked Margin Price = latest_mark_price × margin (settlement-ccy
  // primary via the marked_margin_price twin). Blank until the position is first
  // marked (backend returns null → getSettlementDisplay → dash).
  columnHelper.accessor('marked_margin_price', {
    id: 'marked_margin_price',
    header: 'Last Marked Margin Price',
    cell: (info) => {
      const t = info.row.original;
      const d = getSettlementDisplay(t, 'marked_margin_price');
      if (d === null) return <span className="text-gray-400">-</span>;
      const title = nativeAmountTitle(t, 'marked_margin_price', d.ccy, (n) => n.toFixed(2));
      return (
        <div className={clsx('text-right', title && 'cursor-help')} title={title}>
          <span className="font-medium text-gray-900">{d.value.toFixed(2)}</span>
          <span className=" text-xs text-gray-500 ml-1">{d.ccy}</span>
        </div>
      );
    },
    size: 190,
    meta: { align: 'right' },
  }),
  // LR-522: Last Marked Gross Value = latest_mark_price × qty (settlement-ccy
  // primary). Blank until first marked. Gross twin of Last Marked Margin Value.
  columnHelper.accessor('last_marked_gross_value', {
    id: 'last_marked_gross_value',
    header: 'Last Marked Gross Value',
    cell: (info) => {
      const t = info.row.original;
      const d = getSettlementDisplay(t, 'last_marked_gross_value');
      if (d === null) return <span className="text-gray-400">-</span>;
      const fmt = (n: number): string =>
        n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const title = nativeAmountTitle(t, 'last_marked_gross_value', d.ccy, fmt);
      return (
        <div className={clsx('text-right', title && 'cursor-help')} title={title}>
          <span className="font-medium text-gray-900">{fmt(d.value)}</span>
          <span className=" text-xs text-gray-500 ml-1">{d.ccy}</span>
        </div>
      );
    },
    size: 190,
    meta: { align: 'right' }, // not searchable — converted-display value
  }),
  // LR-522: Last Marked Margin Value = latest_mark_price × margin × qty
  // (settlement-ccy primary via the marked_contract_value twin). Blank until first marked.
  columnHelper.accessor('marked_contract_value', {
    id: 'marked_contract_value',
    header: 'Last Marked Margin Value',
    cell: (info) => {
      const t = info.row.original;
      const d = getSettlementDisplay(t, 'marked_contract_value');
      if (d === null) return <span className="text-gray-400">-</span>;
      const fmt = (n: number): string =>
        n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const title = nativeAmountTitle(t, 'marked_contract_value', d.ccy, fmt);
      return (
        <div className={clsx('text-right', title && 'cursor-help')} title={title}>
          <span className="font-medium text-gray-900">{fmt(d.value)}</span>
          <span className=" text-xs text-gray-500 ml-1">{d.ccy}</span>
        </div>
      );
    },
    size: 195,
    meta: { align: 'right' }, // not searchable — converted-display value
  }),
  // LR-522: Trade Margin Price = booking margin_price (= gross_price × margin),
  // settlement-ccy primary (D4 exception — a price, but its backend twin exists
  // and it sits in the money-column set, so it flips with the value columns).
  // Native figure in the hover tooltip. Was the LR-476 marked view; the marked
  // value now lives in the Last Marked Margin Price column.
  columnHelper.accessor('margin_price', {
    header: 'Trade Margin Price',
    cell: (info) => {
      const t = info.row.original;
      const d = getSettlementDisplay(t, 'margin_price');
      if (d === null) return <span className="text-gray-400">-</span>;
      const title = nativeAmountTitle(t, 'margin_price', d.ccy, (n) => n.toFixed(2));
      return (
        <div className={clsx('text-right', title && 'cursor-help')} title={title}>
          <span className="font-medium text-gray-900">{d.value.toFixed(2)}</span>
          <span className=" text-xs text-gray-500 ml-1">{d.ccy}</span>
        </div>
      );
    },
    size: 160,
    meta: { align: 'right' },
  }),
  // LR-522: Trade Gross Value = gross_price × qty (settlement-ccy primary). Gross
  // (pre-margin) twin of Trade Margin Value.
  columnHelper.accessor('trade_gross_value', {
    id: 'trade_gross_value',
    header: 'Trade Gross Value',
    cell: (info) => {
      const t = info.row.original;
      const d = getSettlementDisplay(t, 'trade_gross_value');
      if (d === null) return <span className="text-gray-400">-</span>;
      const fmt = (n: number): string =>
        n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const title = nativeAmountTitle(t, 'trade_gross_value', d.ccy, fmt);
      return (
        <div className={clsx('text-right', title && 'cursor-help')} title={title}>
          <span className="font-medium text-gray-900">{fmt(d.value)}</span>
          <span className=" text-xs text-gray-500 ml-1">{d.ccy}</span>
        </div>
      );
    },
    size: 160,
    meta: { align: 'right' }, // not searchable — converted-display value
  }),
  // LR-522: Trade Margin Value = booking contract_value (= margin_price × qty),
  // settlement-ccy primary; native figure in the hover tooltip. Was the LR-476
  // marked view; the marked value now lives in the Last Marked Margin Value column.
  columnHelper.accessor('contract_value', {
    header: 'Trade Margin Value',
    cell: (info) => {
      const t = info.row.original;
      const d = getSettlementDisplay(t, 'contract_value');
      if (d === null) return <span className="text-gray-400">-</span>;
      const fmt = (n: number): string =>
        n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const title = nativeAmountTitle(t, 'contract_value', d.ccy, fmt);
      return (
        <div className={clsx('text-right', title && 'cursor-help')} title={title}>
          <span className="font-medium text-gray-900">{fmt(d.value)}</span>
          <span className=" text-xs text-gray-500 ml-1">{d.ccy}</span>
        </div>
      );
    },
    size: 160,
    meta: { align: 'right' }, // not searchable — converted-display value
  }),
  columnHelper.accessor('margin', {
    header: 'Margin %',
    cell: (info) => {
      const value = info.getValue();
      if (value === null || value === undefined) return <span className="text-gray-400">-</span>;
      return (
        <span className="font-medium text-gray-900 text-right block">
          {value.toFixed(2)}%
        </span>
      );
    },
    size: 110,
    meta: { align: 'right' },
  }),
  columnHelper.accessor('daily_earnings', {
    // LR-459: currency-neutral header — the per-row currency is shown inline in
    // the cell (was a hardcoded "(USD)" that mislabelled EUR/GBP cross-currency
    // daily returns, the exact client complaint). Matches the Margin Price /
    // Contract Value headers, which carry no currency suffix.
    header: 'Daily Earnings',
    cell: (info) => {
      const t = info.row.original;
      // LR-474: settlement-ccy primary; native figure in the hover tooltip.
      const d = getSettlementDisplay(t, 'daily_earnings');
      if (d === null) return <span className="text-gray-400">-</span>;
      const fmt = (n: number): string =>
        n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const title = nativeAmountTitle(t, 'daily_earnings', d.ccy, fmt);
      return (
        <div className={clsx('text-right', title && 'cursor-help')} title={title}>
          {/* LR-459: colour by sign — a borrow trade's negative daily return was
              rendered green (matches the Trade Entry form, which colours by sign).
              Sign is taken from the displayed (settlement) value; fx_rate > 0 so
              it matches the native sign. */}
          <span className={`font-medium ${d.value < 0 ? 'text-red-600' : 'text-green-600'}`}>{fmt(d.value)}</span>
          <span className=" text-xs text-gray-500 ml-1">{d.ccy}</span>
        </div>
      );
    },
    size: 185,
    meta: { align: 'right' },
  }),
  // Date-only fields go through formatDate (UTC from the literal Y-M-D): the bare
  // `new Date(v).toLocaleDateString()` path parsed the backend date as UTC midnight
  // and rendered it in the viewer's zone, so a UTC-negative desk saw every date one
  // calendar day early. The format ("Aug 14, 2026") is the trade card's.
  columnHelper.accessor('trade_date', {
    header: 'Trade Date',
    cell: (info) => <span className="text-gray-700">{formatDate(info.getValue())}</span>,
    size: 120,
    meta: { align: 'left' },
  }),
  // LR-406: previously one "Settlement Date" cell rendered open_settlement_date
  // by default and switched to actual_settlement_date once "Set Date" was
  // clicked — one cell, two semantics, confusing. Split into:
  //   1. Open Settlement Date — read-only, when the trade went active.
  //   2. Close Settlement Date — close planning, with "Set Close Date" button.
  columnHelper.accessor('settlement_date', {
    id: 'settlement_date',
    header: 'Open Settlement Date',
    cell: (info) => {
      const value = info.getValue();
      if (!value) return <span className="text-gray-400">-</span>;
      return <span className="text-gray-700">{formatDate(value)}</span>;
    },
    size: 170,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('actual_settlement_date', {
    id: 'actual_settlement_date',
    header: 'Close Settlement Date',
    cell: (info) => {
      const actualDate = info.getValue();
      if (!actualDate) {
        return <span className="text-gray-400">—</span>;
      }
      return (
        <span
          className="text-gray-700"
          title="Close-settlement date. On a matured trade this is when the reverse POSITION TRANSFER is (or was) posted to BO. On an active trade it is only a plan: closure runs once the trade is matured, so it posts on the later of this date and the maturity date — and never at all while the trade has no maturity date."
        >
          {formatDate(actualDate as string)}
        </span>
      );
    },
    size: 160,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('maturity_date', {
    header: 'Maturity Date',
    cell: (info) => {
      const date = info.getValue();
      if (!date) return <span className="text-gray-700">Open</span>;
      return <span className="text-gray-700">{formatDate(date)}</span>;
    },
    size: 130,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('custodian_name', {
    header: 'Custodian',
    cell: (info) => {
      const value = info.getValue();
      if (!value) return <span className="text-gray-400">-</span>;
      return <span className="text-gray-700">{value}</span>;
    },
    size: 140,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('rate_entry_mode', {
    header: 'Rate Mode',
    cell: (info) => {
      const value = info.getValue();
      if (!value) return <span className="text-gray-400">-</span>;
      return <span className="text-gray-700">{value === 'rebate' ? 'Rebate' : 'Fee'}</span>;
    },
    size: 100,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('benchmark_name', {
    header: 'Benchmark',
    cell: (info) => {
      const value = info.getValue();
      if (!value) return <span className="text-gray-400">-</span>;
      return <span className="text-gray-700 uppercase">{value}</span>;
    },
    size: 110,
    meta: { align: 'left' },
  }),
  columnHelper.accessor(RATE_LEG_CELLS.total_rate, {
    id: 'total_rate',
    header: 'Total Rate',
    cell: (info) => {
      const row = info.row.original;
      const value = info.getValue();
      // FLOATING rebate hides the bare total_rate — the meaningful number is benchmark +
      // spread, shown via the dedicated columns. Fee mode and Fixed-rate rebate keep
      // showing it: a fixed row has no spread and no fee, so this is its only rate.
      // Gate on rate_type, NOT on benchmark_name: the latter is a non-nullable column
      // defaulting to 'sofr' (positions.rate.benchmark_name), so it is truthy on a fixed
      // rebate too and blanked every rebate row. Same predicate as RateWaterfall's
      // isFloatingRebate.
      if (row.rate_entry_mode === 'rebate' && row.rate_type === 'floating') {
        return <span className="text-gray-400">-</span>;
      }
      if (value === null || value === undefined) return <span className="text-gray-400">-</span>;
      return (
        <span className="font-medium text-gray-900 text-right block">
          {value.toFixed(2)}%
        </span>
      );
    },
    size: 110,
    meta: { align: 'right' },
  }),
  columnHelper.accessor('spread_bps', {
    header: 'Spread (bps)',
    cell: (info) => {
      const value = info.getValue();
      if (value === null || value === undefined) return <span className="text-gray-400">-</span>;
      return <span className="font-medium text-gray-900 text-right block">{value}</span>;
    },
    size: 110,
    meta: { align: 'right' },
  }),
  columnHelper.accessor('fee_percent', {
    header: 'Fee %',
    cell: (info) => {
      const value = info.getValue();
      if (value === null || value === undefined) return <span className="text-gray-400">-</span>;
      return (
        <span className="font-medium text-gray-900 text-right block">
          {value.toFixed(2)}%
        </span>
      );
    },
    size: 100,
    meta: { align: 'right' },
  }),
  columnHelper.accessor('rate_label', {
    id: 'rate_label',
    header: 'Rate Type',
    cell: (info) => {
      const row = info.row.original;
      const label = info.getValue() ?? (row.trade_type ? getRateLabel(row.trade_type) : null);
      if (!label) return <span className="text-gray-400">-</span>;
      return <span className="text-gray-700">{label}</span>;
    },
    size: 110,
    meta: { align: 'left' },
  }),
  // LR-631: renamed from "Cost of Collateral" - on a pooled fee trade this leg
  // IS the interest the collateral earns in its cash pool. The column id stays
  // `cost_of_collateral`: it keys the API field, the saved column layouts and
  // the CSV header, and renaming it would silently reset every trader's grid.
  columnHelper.accessor(RATE_LEG_CELLS.cost_of_collateral, {
    id: 'cost_of_collateral',
    header: 'Cash Pool Interest Accrual',
    cell: (info) => rateLegCell(info.getValue(), info.row.original.currency),
    size: 140,
    meta: { align: 'right' },
  }),
  columnHelper.accessor(RATE_LEG_CELLS.fee_income, {
    id: 'fee_income',
    header: 'Fee Income',
    cell: (info) => rateLegCell(info.getValue(), info.row.original.currency),
    size: 130,
    meta: { align: 'right' },
  }),
  columnHelper.accessor(RATE_LEG_CELLS.net_amount, {
    id: 'net_amount',
    header: 'Net Amount',
    cell: (info) => rateLegCell(info.getValue(), info.row.original.currency),
    size: 130,
    meta: { align: 'right' },
  }),
  columnHelper.accessor('closing_cash', {
    header: 'Closing Cash',
    cell: (info) => {
      const t = info.row.original;
      const projected = t.closing_cash_projected;
      if (!showsClosingCash(t)) return <span className="text-gray-400">-</span>;
      // LR-474: settlement-ccy primary with an explicit currency code (the old
      // primary had none); native figure in the hover tooltip (cross-ccy only).
      const d = getSettlementDisplay(t, 'closing_cash');
      if (d === null) return <span className="text-gray-400">-</span>;
      const title = nativeAmountTitle(t, 'closing_cash', d.ccy, (n) => formatCurrencyAmount(n));
      return (
        <span
          className={clsx('font-medium text-gray-900 text-right block', title && 'cursor-help')}
          title={title}
        >
          {formatCurrencyAmount(d.value)}
          <span className=" text-xs font-normal text-gray-500 ml-1">{d.ccy}</span>
          {projected && (
            <span className="ml-1 text-xs italic text-gray-500">(projected)</span>
          )}
        </span>
      );
    },
    // LR-626: stacks figure + ccy code + the "(projected)" tag. At the old 160
    // the right-aligned overflow clipped on the LEFT — 452,602.91 rendered as
    // "152,602.91", a wrong number rather than a visibly truncated one.
    size: 215,
    meta: { align: 'right' },
  }),
  columnHelper.accessor('delivery_type', {
    header: 'Delivery Type',
    cell: (info) => {
      const value = info.getValue();
      if (!value) return <span className="text-gray-400">-</span>;
      return <span className="text-gray-700 uppercase">{value}</span>;
    },
    size: 120,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('settlement_venue', {
    header: 'Settlement Venue',
    cell: (info) => {
      const value = info.getValue();
      if (!value) return <span className="text-gray-400">-</span>;
      return <span className="text-gray-700 uppercase">{value}</span>;
    },
    size: 140,
    meta: { align: 'left' },
  }),
  // LR-404: per-leg settlement markers — exposes cash and securities settlement
  // status separately for FOP trades, where each leg can settle in a different
  // place at a different time. DVP rows show both legs settled together (or both
  // pending) since the legs are atomic — the columns are still informative.
  columnHelper.accessor('cash_settled', {
    id: 'cash_settled',
    header: 'Cash Settlement',
    cell: (info) => renderSettlementLeg(
      info.getValue(),
      info.row.original.cash_settled_at,
      info.row.original.cash_settlement_place_name,
    ),
    size: 180,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('securities_settled', {
    id: 'securities_settled',
    header: 'Securities Settlement',
    cell: (info) => renderSettlementLeg(
      info.getValue(),
      info.row.original.securities_settled_at,
      info.row.original.securities_settlement_place_name,
    ),
    size: 200,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('booking_center', {
    header: 'Booking Center',
    cell: (info) => {
      const value = info.getValue();
      if (!value) return <span className="text-gray-400">-</span>;
      return <span className="text-gray-700">{value}</span>;
    },
    size: 130,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('custodian_account', {
    header: 'Custodian Account',
    cell: (info) => {
      const value = info.getValue();
      if (!value) return <span className="text-gray-400">-</span>;
      return <span className="text-xs text-gray-700">{value}</span>;
    },
    size: 150,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('counterparty_account', {
    header: 'Counterparty Account',
    cell: (info) => {
      const value = info.getValue();
      if (!value) return <span className="text-gray-400">-</span>;
      return <span className="text-xs text-gray-700">{value}</span>;
    },
    size: 160,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('collateral_type', {
    header: 'Collateral Type',
    cell: (info) => {
      const value = info.getValue();
      if (!value) return <span className="text-gray-400">-</span>;
      return <span className="text-gray-700">{COLLATERAL_TYPE_LABELS[value as CollateralType] ?? value}</span>;
    },
    size: 140,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('collateral_isin', {
    header: 'Collateral ISIN',
    cell: (info) => {
      const value = info.getValue();
      if (!value) return <span className="text-gray-400">-</span>;
      return <span className="text-xs text-gray-700">{value}</span>;
    },
    size: 140,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('collateral_quantity', {
    header: 'Collateral Qty',
    cell: (info) => {
      const value = info.getValue();
      if (value === undefined || value === null) return <span className="text-gray-400">-</span>;
      return <span className="text-xs text-gray-700">{value.toLocaleString('en-US')}</span>;
    },
    size: 130,
    meta: { align: 'right' },
  }),
  columnHelper.accessor('collateral_value', {
    header: 'Collateral Value',
    cell: (info) => {
      const value = info.getValue();
      if (value === undefined || value === null) return <span className="text-gray-400">-</span>;
      return (
        <span className="text-xs text-gray-700">
          {value.toLocaleString('en-US', { maximumFractionDigits: 2 })}
        </span>
      );
    },
    size: 150,
    meta: { align: 'right' },
  }),
  columnHelper.accessor('pre_delivery_party', {
    header: 'Pre-Delivery',
    cell: (info) => {
      const value = info.getValue();
      if (!value) return <span className="text-gray-400">-</span>;
      const display: Record<string, string> = {
        us: 'We first',
        counterparty: 'CP first',
        simultaneous: 'Simultaneous',
      };
      return <span className="text-xs text-gray-700">{display[value] || value}</span>;
    },
    size: 130,
    meta: { align: 'left' },
  }),
  // LR-474: label disambiguated from the new Settlement Ccy column. The column
  // id stays `currency` — it is persisted in user column state (order/visibility),
  // so renaming the id would need a store migration for zero gain.
  columnHelper.accessor('currency', {
    header: 'Security Ccy',
    cell: (info) => {
      const value = info.getValue();
      return <span className=" text-xs text-gray-700">{value}</span>;
    },
    size: 90,
    meta: { align: 'left' },
  }),
  // LR-474: currency the trade actually settles in (falls back to the security
  // currency for same-ccy trades) — the bucket key for the per-ccy totals and
  // the basis of the currency pill filter.
  columnHelper.accessor('settlement_currency', {
    id: 'settlement_currency',
    header: 'Settlement Ccy',
    cell: (info) => {
      const eff = getEffectiveSettlementCurrency(info.row.original);
      // Muted dash like the money cells (r1 #9) — eff is only empty when the
      // row carries neither settlement nor security currency.
      if (!eff) return <span className="text-gray-400">-</span>;
      return <span className=" text-xs text-gray-700">{eff}</span>;
    },
    size: 110,
    meta: { align: 'left' },
  }),
  // Timestamps read in canonical UTC (the desk's one-timezone rule) with the
  // viewer-local companion on hover — the same pair the Current Market Price
  // "as of" tooltip uses; the old cell printed the viewer's local 12-hour clock
  // with no zone marker.
  columnHelper.accessor('created_at', {
    header: 'Created At',
    cell: (info) => {
      const date = info.getValue();
      return (
        <span className="text-xs text-gray-500" title={`Local time: ${formatDateTimeLocal(date)}`}>
          {formatDateTimeUtc(date)}
        </span>
      );
    },
    size: 150,
    meta: { align: 'left' },
  }),
  columnHelper.accessor('updated_at', {
    header: 'Updated At',
    cell: (info) => {
      const date = info.getValue();
      return (
        <span className="text-xs text-gray-500" title={`Local time: ${formatDateTimeLocal(date)}`}>
          {formatDateTimeUtc(date)}
        </span>
      );
    },
    size: 150,
    meta: { align: 'left' },
  }),
];
};
